/**
 * TableExamples.tsx
 * Comprehensive showcase of HeroUI Table component with trading journal context
 * 
 * Demonstrates data tables, selection, sorting, and customization options
 * with practical examples relevant to trading applications
 */

import React, { useState } from 'react';
import { PlaygroundPageLayout } from '../shared';
import { Card, CardHeader, CardBody } from '@enfomentis/ui';
import { Badge } from '@enfomentis/ui';
import { Divider } from '@enfomentis/ui';
import { Table, TableHeader, TableBody, TableColumn, TableRow, TableCell } from '@enfomentis/ui';
import type { Selection, SortDescriptor } from '@heroui/react';
import { getKeyValue } from '@heroui/react';
import { Button } from '@enfomentis/ui';
import { Chip } from '@enfomentis/ui';
import { Link } from '@enfomentis/ui';

// Sample trading data for examples
const sampleTrades = [
  { id: 1, symbol: 'AAPL', type: 'BUY', quantity: 100, price: 150.25, date: '2024-01-15', profit: 245.50, status: 'closed' },
  { id: 2, symbol: 'GOOGL', type: 'SELL', quantity: 50, price: 2800.75, date: '2024-01-16', profit: -120.25, status: 'closed' },
  { id: 3, symbol: 'TSLA', type: 'BUY', quantity: 75, price: 185.50, date: '2024-01-17', profit: 389.75, status: 'open' },
  { id: 4, symbol: 'MSFT', type: 'BUY', quantity: 200, price: 380.25, date: '2024-01-18', profit: 156.50, status: 'closed' },
  { id: 5, symbol: 'AMZN', type: 'SELL', quantity: 25, price: 3200.50, date: '2024-01-19', profit: -89.25, status: 'open' },
];

const samplePortfolio = [
  { id: 1, symbol: 'AAPL', shares: 500, avgPrice: 145.25, currentPrice: 152.30, value: 76150, gain: 3525 },
  { id: 2, symbol: 'GOOGL', shares: 150, avgPrice: 2750.50, currentPrice: 2820.75, value: 423112.50, gain: 10537.50 },
  { id: 3, symbol: 'TSLA', shares: 300, avgPrice: 180.75, currentPrice: 195.25, value: 58575, gain: 4350 },
  { id: 4, symbol: 'MSFT', shares: 400, avgPrice: 375.25, currentPrice: 385.50, value: 154200, gain: 4100 },
];

const summaryData = [
  { id: 1, label: 'Total Trades', value: '247' },
  { id: 2, label: 'Winning Trades', value: '156 (63.2%)', isWinning: true },
  { id: 3, label: 'Total P&L', value: '$12,847.50', isProfit: true },
  { id: 4, label: 'Average Win', value: '$145.25' },
  { id: 5, label: 'Average Loss', value: '$89.75' },
];

const optionsData = [
  { id: 1, strike: '$145', callBid: '$8.50', callAsk: '$8.75', callVol: '245', putBid: '$2.25', putAsk: '$2.50', putVol: '156' },
  { id: 2, strike: '$150', callBid: '$5.75', callAsk: '$6.00', callVol: '389', putBid: '$4.25', putAsk: '$4.50', putVol: '298' },
  { id: 3, strike: '$155', callBid: '$3.50', callAsk: '$3.75', callVol: '156', putBid: '$7.25', putAsk: '$7.50', putVol: '89' },
];

const riskData = [
  { id: 1, metric: 'Portfolio Beta', current: '1.23', limit: '1.50', status: 'good' },
  { id: 2, metric: 'VaR (95%, 1d)', current: '$2,847', limit: '$5,000', status: 'good' },
  { id: 3, metric: 'Leverage Ratio', current: '2.8:1', limit: '3:1', status: 'caution' },
  { id: 4, metric: 'Max Drawdown', current: '-8.5%', limit: '-10%', status: 'watch' },
];

export const TableExamples: React.FC = () => {
  // State for interactive examples
  const [selectedTrades, setSelectedTrades] = useState<Selection>(new Set());
  const [selectedPortfolio, setSelectedPortfolio] = useState<Selection>(new Set());
  const [sortDescriptor, setSortDescriptor] = useState<SortDescriptor>({
    column: "symbol",
    direction: "ascending"
  });

  // Column definitions for different tables
  const tradeColumns = [
    {name: "Symbol", uid: "symbol", sortable: false},
    {name: "Type", uid: "type", sortable: false},
    {name: "Quantity", uid: "quantity", sortable: false},
    {name: "Price", uid: "price", sortable: false},
    {name: "Date", uid: "date", sortable: false},
    {name: "Status", uid: "status", sortable: false},
  ];

  const tradeHistoryColumns = [
    {name: "Symbol", uid: "symbol", sortable: true},
    {name: "Type", uid: "type", sortable: false},
    {name: "Quantity", uid: "quantity", sortable: true},
    {name: "Price", uid: "price", sortable: true},
    {name: "P&L", uid: "profit", sortable: true},
    {name: "Date", uid: "date", sortable: true},
  ];

  const portfolioColumns = [
    {name: "Symbol", uid: "symbol", sortable: true},
    {name: "Shares", uid: "shares", sortable: true},
    {name: "Avg Price", uid: "avgPrice", sortable: true},
    {name: "Current Price", uid: "currentPrice", sortable: true},
    {name: "Market Value", uid: "value", sortable: true},
    {name: "Total Gain/Loss", uid: "gain", sortable: true},
  ];

  const watchlistColumns = [
    {name: "Symbol", uid: "symbol", sortable: false},
    {name: "Price", uid: "price", sortable: false},
    {name: "Change", uid: "change", sortable: false},
    {name: "Volume", uid: "volume", sortable: false},
  ];

  const summaryColumns = [
    {name: "Label", uid: "label", sortable: false},
    {name: "Value", uid: "value", sortable: false},
  ];

  const optionsColumns = [
    {name: "Strike", uid: "strike", sortable: false},
    {name: "Call Bid", uid: "callBid", sortable: false},
    {name: "Call Ask", uid: "callAsk", sortable: false},
    {name: "Call Vol", uid: "callVol", sortable: false},
    {name: "Put Bid", uid: "putBid", sortable: false},
    {name: "Put Ask", uid: "putAsk", sortable: false},
    {name: "Put Vol", uid: "putVol", sortable: false},
  ];

  const riskColumns = [
    {name: "Risk Metric", uid: "metric", sortable: false},
    {name: "Current", uid: "current", sortable: false},
    {name: "Limit", uid: "limit", sortable: false},
    {name: "Status", uid: "status", sortable: false},
  ];

  const formatCurrency = (value: number) => {
    return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(value);
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', { 
      year: 'numeric', 
      month: 'short', 
      day: 'numeric' 
    });
  };

  // Cell rendering functions
  const renderTradeCell = React.useCallback((item: Record<string, unknown>, columnKey: React.Key) => {
    const cellValue = getKeyValue(item, columnKey as string);

    switch (columnKey) {
      case "type":
        return (
          <Chip 
            color={cellValue === 'BUY' ? 'success' : 'danger'} 
            size="sm" 
            variant="flat"
          >
            {cellValue}
          </Chip>
        );
      case "quantity":
        return cellValue.toLocaleString();
      case "price":
        return formatCurrency(cellValue);
      case "date":
        return formatDate(cellValue);
      case "status":
        return (
          <Chip 
            color={cellValue === 'closed' ? 'default' : 'warning'} 
            size="sm"
          >
            {cellValue}
          </Chip>
        );
      case "profit":
        return (
          <span className={cellValue >= 0 ? 'text-success' : 'text-danger'}>
            {formatCurrency(cellValue)}
          </span>
        );
      default:
        return cellValue;
    }
  }, []);

  const renderPortfolioCell = React.useCallback((item: Record<string, unknown>, columnKey: React.Key) => {
    const cellValue = getKeyValue(item, columnKey as string);

    switch (columnKey) {
      case "symbol":
        return <div className="font-medium">{cellValue}</div>;
      case "shares":
        return cellValue.toLocaleString();
      case "avgPrice":
      case "currentPrice":
        return formatCurrency(cellValue);
      case "value":
        return <div className="font-medium">{formatCurrency(cellValue)}</div>;
      case "gain":
        return (
          <div className={`font-medium ${cellValue >= 0 ? 'text-success' : 'text-danger'}`}>
            {formatCurrency(cellValue)}
          </div>
        );
      default:
        return cellValue;
    }
  }, []);

  const renderSummaryCell = React.useCallback((item: Record<string, unknown>, columnKey: React.Key) => {
    const cellValue = getKeyValue(item, columnKey as string);

    switch (columnKey) {
      case "label":
        return <span className="text-text-secondary">{cellValue}</span>;
      case "value":
        if (item['isWinning']) {
          return <span className="font-medium text-success">{cellValue}</span>;
        }
        if (item['isProfit']) {
          return <span className="font-medium text-success">{cellValue}</span>;
        }
        return <span className="font-medium">{cellValue}</span>;
      default:
        return cellValue;
    }
  }, []);

  const renderOptionsCell = React.useCallback((item: Record<string, unknown>, columnKey: React.Key) => {
    const cellValue = getKeyValue(item, columnKey as string);

    switch (columnKey) {
      case "strike":
        return <span className="font-medium">{cellValue}</span>;
      default:
        return cellValue;
    }
  }, []);

  const renderRiskCell = React.useCallback((item: Record<string, unknown>, columnKey: React.Key) => {
    const cellValue = getKeyValue(item, columnKey as string);

    switch (columnKey) {
      case "metric":
        return <span className="font-medium">{cellValue}</span>;
      case "status": {
        const statusConfig = {
          good: { color: 'success' as const, label: 'Good' },
          caution: { color: 'warning' as const, label: 'Caution' },
          watch: { color: 'warning' as const, label: 'Watch' },
        };
        const config = statusConfig[cellValue as keyof typeof statusConfig];
        return (
          <Chip color={config.color} size="sm">
            {config.label}
          </Chip>
        );
      }
      default:
        return cellValue;
    }
  }, []);

  // Sorted portfolio data
  const sortedPortfolio = React.useMemo(() => {
    return [...samplePortfolio].sort((a, b) => {
      const first = a[sortDescriptor.column as keyof typeof a];
      const second = b[sortDescriptor.column as keyof typeof b];
      let cmp = 0;

      if (typeof first === 'string' && typeof second === 'string') {
        cmp = first.localeCompare(second);
      } else if (typeof first === 'number' && typeof second === 'number') {
        cmp = first - second;
      }

      return sortDescriptor.direction === "descending" ? -cmp : cmp;
    });
  }, [sortDescriptor]);

  return (
    <PlaygroundPageLayout>
      <div className="space-y-8">
        
        {/* Header */}
        <div className="text-center space-y-4">
          <div className="inline-flex items-center gap-3">
            <div className="p-2 bg-primary-100 dark:bg-primary-900/30 rounded-lg">
              <div className="w-6 h-6 border border-border rounded bg-surface"></div>
            </div>
            <h1 className="text-3xl font-bold text-text-primary">Table Component</h1>
          </div>
          <p className="text-lg text-text-secondary max-w-2xl mx-auto">
            Comprehensive data tables with selection, sorting, and virtualization support.
            Perfect for displaying structured data with interactive features and accessibility.
          </p>
          <div className="flex items-center justify-center gap-2">
            <Badge color="primary" variant="flat">HeroUI Based</Badge>
            <Badge color="secondary" variant="flat">Data Display</Badge>
            <Badge color="success" variant="flat">Theme Integrated</Badge>
          </div>
        </div>

        {/* Basic Table */}
        <Card>
          <CardHeader>
            <div className="space-y-2">
              <h2 className="text-xl font-semibold text-text-primary">Basic Data Table</h2>
              <p className="text-text-secondary">Simple table with trading data and basic styling</p>
            </div>
          </CardHeader>
          <CardBody className="space-y-6">
            
            <div className="space-y-4">
              <h3 className="text-lg font-medium text-text-primary">Recent Trades</h3>
              <Table aria-label="Recent trades table">
                <TableHeader columns={tradeColumns}>
                  {(column) => (
                    <TableColumn 
                      key={column.uid}
                      allowsSorting={column.sortable}
                    >
                      {column.name}
                    </TableColumn>
                  )}
                </TableHeader>
                <TableBody items={sampleTrades}>
                  {(item) => (
                    <TableRow key={String(item['id'])}>
                      {(columnKey) => (
                        <TableCell>{renderTradeCell(item, columnKey)}</TableCell>
                      )}
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </div>

          </CardBody>
        </Card>

        {/* Table with Selection */}
        <Card>
          <CardHeader>
            <div className="space-y-2">
              <h2 className="text-xl font-semibold text-text-primary">Table with Selection</h2>
              <p className="text-text-secondary">Multi-select table with selection management</p>
            </div>
          </CardHeader>
          <CardBody className="space-y-6">
            
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <h3 className="text-lg font-medium text-text-primary">Trade History</h3>
                <div className="flex items-center gap-2">
                  <span className="text-sm text-text-secondary">
                    {selectedTrades === "all" ? sampleTrades.length : (selectedTrades as Set<React.Key>).size} of {sampleTrades.length} selected
                  </span>
                  <Button 
                    size="sm" 
                    variant="bordered"
                    onPress={() => setSelectedTrades(new Set())}
                    isDisabled={selectedTrades === "all" ? false : (selectedTrades as Set<React.Key>).size === 0}
                  >
                    Clear Selection
                  </Button>
                </div>
              </div>
              
              <Table 
                aria-label="Trade history with selection"
                selectionMode="multiple"
                selectedKeys={selectedTrades}
                onSelectionChange={setSelectedTrades}
                isStriped
              >
                <TableHeader columns={tradeHistoryColumns}>
                  {(column) => (
                    <TableColumn 
                      key={column.uid}
                      allowsSorting={column.sortable}
                    >
                      {column.name}
                    </TableColumn>
                  )}
                </TableHeader>
                <TableBody items={sampleTrades}>
                  {(item) => (
                    <TableRow key={String(item['id'])}>
                      {(columnKey) => (
                        <TableCell>{renderTradeCell(item, columnKey)}</TableCell>
                      )}
                    </TableRow>
                  )}
                </TableBody>
              </Table>

              {(selectedTrades === "all" || (selectedTrades as Set<React.Key>).size > 0) && (
                <div className="p-4 bg-primary-50 dark:bg-primary-900/20 rounded-lg border border-primary-200 dark:border-primary-800">
                  <p className="text-primary-700 dark:text-primary-300">
                    <strong>Selected trades:</strong> {selectedTrades === "all" ? "All" : Array.from(selectedTrades as Set<React.Key>).join(', ')}
                  </p>
                </div>
              )}
            </div>

          </CardBody>
        </Card>

        {/* Sortable Table */}
        <Card>
          <CardHeader>
            <div className="space-y-2">
              <h2 className="text-xl font-semibold text-text-primary">Sortable Table</h2>
              <p className="text-text-secondary">Table with column sorting capabilities</p>
            </div>
          </CardHeader>
          <CardBody className="space-y-6">
            
            <div className="space-y-4">
              <h3 className="text-lg font-medium text-text-primary">Portfolio Holdings</h3>
              <Table 
                aria-label="Portfolio holdings with sorting"
                selectionMode="single"
                selectedKeys={selectedPortfolio}
                onSelectionChange={setSelectedPortfolio}
                sortDescriptor={sortDescriptor}
                onSortChange={setSortDescriptor}
              >
                <TableHeader columns={portfolioColumns}>
                  {(column) => (
                    <TableColumn 
                      key={column.uid}
                      allowsSorting={column.sortable}
                    >
                      {column.name}
                    </TableColumn>
                  )}
                </TableHeader>
                <TableBody items={sortedPortfolio}>
                  {(item) => (
                    <TableRow key={String(item['id'])}>
                      {(columnKey) => (
                        <TableCell>{renderPortfolioCell(item, columnKey)}</TableCell>
                      )}
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </div>

          </CardBody>
        </Card>

        {/* Empty State Table */}
        <Card>
          <CardHeader>
            <div className="space-y-2">
              <h2 className="text-xl font-semibold text-text-primary">Empty State & Loading</h2>
              <p className="text-text-secondary">Table states for empty data and loading scenarios</p>
            </div>
          </CardHeader>
          <CardBody className="space-y-6">
            
            <div className="space-y-4">
              <h3 className="text-lg font-medium text-text-primary">No Watchlist Items</h3>
              <Table aria-label="Empty watchlist table">
                <TableHeader columns={watchlistColumns}>
                  {(column) => (
                    <TableColumn key={column.uid}>
                      {column.name}
                    </TableColumn>
                  )}
                </TableHeader>
                <TableBody 
                  items={[] as Record<string, unknown>[]}
                  emptyContent="No stocks in your watchlist yet. Add some symbols to get started."
                >
                  {(item: Record<string, unknown>) => (
                    <TableRow key={String(item['id'])}>
                      {(columnKey) => (
                        <TableCell>{getKeyValue(item, columnKey as string)}</TableCell>
                      )}
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </div>

            <Divider />

            <div className="space-y-4">
              <h3 className="text-lg font-medium text-text-primary">Loading State</h3>
              <Table aria-label="Loading market data">
                <TableHeader columns={watchlistColumns}>
                  {(column) => (
                    <TableColumn key={column.uid}>
                      {column.name}
                    </TableColumn>
                  )}
                </TableHeader>
                <TableBody 
                  items={[] as Record<string, unknown>[]}
                  isLoading 
                  loadingContent="Loading market data..."
                >
                  {(item: Record<string, unknown>) => (
                    <TableRow key={String(item['id'])}>
                      {(columnKey) => (
                        <TableCell>{getKeyValue(item, columnKey as string)}</TableCell>
                      )}
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </div>

          </CardBody>
        </Card>

        {/* Compact Table */}
        <Card>
          <CardHeader>
            <div className="space-y-2">
              <h2 className="text-xl font-semibold text-text-primary">Compact Layout</h2>
              <p className="text-text-secondary">Space-efficient table design for dense data</p>
            </div>
          </CardHeader>
          <CardBody className="space-y-6">
            
            <div className="space-y-4">
              <h3 className="text-lg font-medium text-text-primary">Quick Trade Summary</h3>
              <Table 
                aria-label="Compact trade summary"
                removeWrapper
                hideHeader
                className="max-w-md"
              >
                <TableHeader columns={summaryColumns}>
                  {(column) => (
                    <TableColumn key={column.uid}>
                      {column.name}
                    </TableColumn>
                  )}
                </TableHeader>
                <TableBody items={summaryData}>
                  {(item) => (
                    <TableRow key={String(item['id'])}>
                      {(columnKey) => (
                        <TableCell>{renderSummaryCell(item, columnKey)}</TableCell>
                      )}
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </div>

          </CardBody>
        </Card>

        {/* Trading Application Examples */}
        <Card>
          <CardHeader>
            <div className="space-y-2">
              <h2 className="text-xl font-semibold text-text-primary">Trading Application Examples</h2>
              <p className="text-text-secondary">Real-world table scenarios for trading platforms</p>
            </div>
          </CardHeader>
          <CardBody className="space-y-6">
            
            <div className="space-y-4">
              <h3 className="text-lg font-medium text-text-primary">Options Chain (Basic)</h3>
              <Table aria-label="Options chain" isStriped>
                <TableHeader columns={optionsColumns}>
                  {(column) => (
                    <TableColumn key={column.uid}>
                      {column.name}
                    </TableColumn>
                  )}
                </TableHeader>
                <TableBody items={optionsData}>
                  {(item) => (
                    <TableRow key={String(item['id'])}>
                      {(columnKey) => (
                        <TableCell>{renderOptionsCell(item, columnKey)}</TableCell>
                      )}
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </div>

            <Divider />

            <div className="space-y-4">
              <h3 className="text-lg font-medium text-text-primary">Risk Analysis</h3>
              <Table aria-label="Risk analysis">
                <TableHeader columns={riskColumns}>
                  {(column) => (
                    <TableColumn key={column.uid}>
                      {column.name}
                    </TableColumn>
                  )}
                </TableHeader>
                <TableBody items={riskData}>
                  {(item) => (
                    <TableRow key={String(item['id'])}>
                      {(columnKey) => (
                        <TableCell>{renderRiskCell(item, columnKey)}</TableCell>
                      )}
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </div>

          </CardBody>
        </Card>

        {/* Accessibility & Best Practices */}
        <Card>
          <CardHeader>
            <div className="space-y-2">
              <h2 className="text-xl font-semibold text-text-primary">Accessibility & Best Practices</h2>
              <p className="text-text-secondary">Guidelines for accessible and user-friendly tables</p>
            </div>
          </CardHeader>
          <CardBody className="space-y-4">
            
            <div className="space-y-3">
              <h3 className="text-lg font-medium text-text-primary">Table Component Usage</h3>
              <div className="p-4 bg-primary-50 dark:bg-primary-900/20 rounded-lg border border-primary-200 dark:border-primary-800">
                <p className="text-primary-700 dark:text-primary-300">
                  <strong>Note:</strong> HeroUI Tables are designed for basic data display with sorting and selection. 
                  For advanced table features like filtering, column resizing, grouping, and complex data operations, 
                  we use AG-Grid which is demonstrated in the <Link href="/playground/data">main Data section</Link> of this application.
                </p>
              </div>
            </div>

            <Divider />

            <div className="space-y-3">
              <h3 className="text-lg font-medium text-text-primary">Accessibility Features</h3>
              <ul className="text-text-secondary space-y-2 list-disc list-inside">
                <li>Full keyboard navigation (arrow keys, typeahead search)</li>
                <li>ARIA grid role with live region announcements for selections</li>
                <li>Support for row headers and column associations</li>
                <li>Automatic scrolling during keyboard navigation</li>
                <li>Touch-friendly selection modes (long press to enter selection)</li>
                <li>Support for disabled rows with proper ARIA attributes</li>
              </ul>
            </div>

            <Divider />

            <div className="space-y-3">
              <h3 className="text-lg font-medium text-text-primary">Usage Guidelines</h3>
              <ul className="text-text-secondary space-y-2 list-disc list-inside">
                <li>Always provide meaningful <code className="px-2 py-1 bg-default-100 rounded text-sm">aria-label</code> descriptions</li>
                <li>Use <code className="px-2 py-1 bg-default-100 rounded text-sm">isStriped</code> for better row differentiation with large datasets</li>
                <li>Enable sorting with <code className="px-2 py-1 bg-default-100 rounded text-sm">allowsSorting</code> for appropriate columns</li>
                <li>Use <code className="px-2 py-1 bg-default-100 rounded text-sm">selectionMode</code> to control interaction patterns</li>
                <li>Leverage <code className="px-2 py-1 bg-default-100 rounded text-sm">disabledKeys</code> for rows that shouldn't be selectable</li>
                <li>Provide clear empty states and loading feedback</li>
              </ul>
            </div>

            <Divider />

            <div className="space-y-3">
              <h3 className="text-lg font-medium text-text-primary">Performance Considerations</h3>
              <ul className="text-text-secondary space-y-2 list-disc list-inside">
                <li>Use <code className="px-2 py-1 bg-default-100 rounded text-sm">isVirtualized</code> for efficient rendering of large datasets</li>
                <li>React Aria collections provide optimized rendering with caching</li>
                <li>Consider pagination or server-side data management for very large datasets</li>
                <li>Memoize expensive cell rendering computations with React.useCallback</li>
                <li>Use stable keys for optimal React reconciliation performance</li>
              </ul>
            </div>

          </CardBody>
        </Card>

      </div>
    </PlaygroundPageLayout>
  );
};